import { Module } from "vuex"
import axios from "axios"
import { Device } from "@/types/moj-elektro"
import { RootState } from "@/store"

export interface ExternalDevicesState {
    devices: Device[]
    filteredDevices: Device[]
    isLoading: boolean
    showAddModal: boolean
    eimm: string
    errorMessage: string | null
}

const state: ExternalDevicesState = {
    devices: [],
    filteredDevices: [],
    isLoading: false,
    showAddModal: false,
    eimm: "",
    errorMessage: null,
}

const externalDevicesModule: Module<ExternalDevicesState, RootState> = {
    namespaced: true,
    state,
    mutations: {
        setDevices(state, devices: Device[]) {
            state.devices = devices
            state.filteredDevices = devices
        },
        setFilteredDevices(state, devices: Device[]) {
            state.filteredDevices = devices
        },
        setLoading(state, isLoading: boolean) {
            state.isLoading = isLoading
        },
        addDevice(state, { device, token }) {
            const existingDevice = state.devices.find((d) => d.id === device.id)

            if (existingDevice) {
                const tokenExists = existingDevice.credentials.some(
                    (credential) => credential.token_id === token.id,
                )

                if (!tokenExists) {
                    existingDevice.credentials.push({
                        token: token.token,
                        token_id: token.id,
                    })
                }
            } else {
                device.credentials = [{ token: token.token, token_id: token.id }]
                state.devices.push(device)
            }

            state.filteredDevices = state.devices.filter((d) =>
                d.credentials.some((cred) => cred.token_id === token.id),
            )
        },
        deleteDevice(state, { deviceId, tokenId }) {
            const device = state.devices.find((d) => d.id === deviceId)

            if (device) {
                if (device.credentials.length === 1) {
                    state.devices = state.devices.filter((d) => d.id !== deviceId)
                } else {
                    device.credentials = device.credentials.filter((c) => c.token_id !== tokenId)
                }

                state.filteredDevices = state.devices
            }
        },
        setShowAddModal(state, show: boolean) {
            state.showAddModal = show
        },
        setEimm(state, value: string) {
            state.eimm = value
        },
        setErrorMessage(state, value: string | null) {
            state.errorMessage = value
        },
    },
    actions: {
        async fetchAllDevices({ commit, dispatch }, errorMessageText: string) {
            commit("setLoading", true)

            try {
                const response = await axios.get("/abstract-devices-all")

                commit("setDevices", response.data.devices ?? [])
            } catch (error) {
                console.error(errorMessageText, error)

                dispatch(
                    "toastMessageModule/showMessage",
                    { message: errorMessageText, color: "red" },
                    { root: true },
                )
            } finally {
                commit("setLoading", false)
            }
        },
        async addDevice({ commit, dispatch, rootGetters }, errorMessageText: string) {
            const token = rootGetters["externalTokens/getSelectedToken"]

            if (!state.eimm || !token?.id) {
                return
            }

            commit("setLoading", true)

            const device = {
                eimm: state.eimm,
                token_id: token?.id,
                abstract_device_type_id: 1,
                credentials: [{ token: token?.token, token_id: token?.id }],
            }

            try {
                const response = await axios.post("/abstract-device", { data: device })
                commit("addDevice", { device: response.data.addDevice, token })
                commit("setErrorMessage", null)
            } catch (error: any) {
                console.error(errorMessageText, error)

                commit("setErrorMessage", error?.message)

                dispatch(
                    "toastMessageModule/showMessage",
                    { message: errorMessageText, color: "red" },
                    { root: true },
                )
            } finally {
                commit("setLoading", false)
            }
        },
        async deleteDevice(
            { commit, dispatch },
            payload: { deviceId: number; tokenId: number; errorMessageText: string },
        ) {
            const { deviceId, tokenId, errorMessageText } = payload

            commit("setLoading", true)

            try {
                await axios.delete(`/abstract-device/${deviceId}/token/${tokenId}`)

                commit("deleteDevice", { deviceId, tokenId })
            } catch (error) {
                console.error(errorMessageText, error)

                dispatch(
                    "toastMessageModule/showMessage",
                    { message: errorMessageText, color: "red" },
                    { root: true },
                )
            } finally {
                commit("setLoading", false)
            }
        },
        deleteDevicesByToken({ commit }, tokenId: number) {
            const devicesToDelete = state.devices.filter((device) =>
                device.credentials.some((cred) => cred.token_id === tokenId),
            )

            devicesToDelete.forEach((device) => {
                commit("deleteDevice", { deviceId: device.id, tokenId })
            })
        },
        filterDevicesByToken({ commit, state }, tokenId: number) {
            const filteredDevices = state.devices?.filter((device) =>
                device.credentials.some((c) => c.token_id === tokenId),
            )

            commit("setFilteredDevices", filteredDevices)
        },
        selectAllDevices({ commit }) {
            commit("setFilteredDevices", state.devices)
        },
        setShowAddModal({ commit }, show: boolean) {
            commit("setShowAddModal", show)
        },
        setEimm({ commit }, value: string) {
            commit("setEimm", value)
        },
        setErrorMessage({ commit }, value: string | null) {
            commit("setErrorMessage", value)
        },
    },
    getters: {
        getAllDevices: (state) => state.devices,
        getFilteredDevices: (state) => state.filteredDevices,
        getIsLoading: (state) => state.isLoading,
        getSelectedDevice: (state) => (id: number) => {
            if (id === null) {
                return
            }

            return state.devices.find((device) => device.id === id)
        },
        getShowAddModal: (state): boolean => state.showAddModal,
        getEimm: (state): string => state.eimm,
        getErrorMessage: (state): string | null => state.errorMessage,
    },
}

export default externalDevicesModule
